import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { SkeletonDonutChartCard } from '../../util/skeleton/Skeleton';


const PopularPlansChart = ({ popularPlansData }) => {
  const isLoading = !popularPlansData;

  const labels = useMemo(() => popularPlansData?.labels ?? [], [popularPlansData]);
  const series = useMemo(() => popularPlansData?.series ?? [], [popularPlansData]);
  const totalUsers = useMemo(
    () => popularPlansData?.meta?.total_users ?? series.reduce((a, b) => a + b, 0),
    [popularPlansData, series]
  );

  // Format range: if `to` is midnight (likely exclusive), show previous day
  const rangeText = useMemo(() => {
    const range = popularPlansData?.meta?.range;
    if (!range?.from || !range?.to) return '';
    const parse = (s) => new Date(s.replace(' ', 'T')); // treat as local
    const from = parse(range.from);

    let to = parse(range.to);
    if (to.getHours() === 0 && to.getMinutes() === 0 && to.getSeconds() === 0) {
      to = new Date(to.getTime() - 1000); // show previous day for exclusives
    }

    const fmt = new Intl.DateTimeFormat('en-UG', { year: 'numeric', month: 'short', day: 'numeric' });
    return `${fmt.format(from)} – ${fmt.format(to)}`;
  }, [popularPlansData]);

  const palette = ['#10b981', '#f59e0b', '#ef4444', '#0ea5e9', '#6366f1', '#22c55e', '#a78bfa', '#f97316', '#06b6d4', '#84cc16'];
  const colors = useMemo(() => palette.slice(0, Math.max(labels.length, 1)), [labels.length]);

  const chartOptions = useMemo(() => ({
    chart: {
      type: 'donut',
      animations: { enabled: true, easing: 'easeinout', speed: 500 },
      toolbar: { show: false },
    },
    labels,
    colors,
    dataLabels: {
      enabled: true,
      style: { colors: ['#fff'], fontSize: '12px' },
      formatter: (val) => `${val.toFixed(1)}%`,
    },
    legend: { show: false },
    tooltip: { y: { formatter: (val) => `${val} users` } },
    plotOptions: {
      pie: {
        expandOnClick: true,
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: { show: false },
            value: {
              show: true,
              fontSize: '16px',
              color: '#fff',
              formatter: (val) => `${val} users`,
            },
            total: {
              show: true,
              label: 'Total',
              color: '#cbd5e1',
              formatter: () => `${totalUsers} users`,
            },
          },
        },
      },
    },
    stroke: { show: true, width: 1, colors: ['#1f2937'] },
  }), [labels, colors, totalUsers]);

  if (isLoading) return <SkeletonDonutChartCard />;

  return (
    <div className="card custom-card overflow-hidden">
      <div className="card-body p-3">
        <h6 className="card-title mb-2">Top Popular WiFi Plans</h6>
        <p className="text-muted small mb-3">
          Top accessed plans by user count{rangeText ? ` between ${rangeText}` : ''}
        </p>

        <div className="d-flex align-items-center">
          {/* Chart */}
          <div style={{ flex: 1 }}>
            <Chart options={chartOptions} series={series} type="donut" height={200} />
          </div>

          {/* Custom Legend */}
          <div className="ml-4" style={{ flexShrink: 0 }}>
            {labels.map((label, i) => (
              <div key={label + i} className="d-flex align-items-center mb-2">
                <span
                  className="mr-2"
                  style={{
                    display: 'inline-block',
                    width: 12,
                    height: 12,
                    backgroundColor: colors[i % colors.length],
                    borderRadius: '50%',
                  }}
                />
                <span className="small text-white-50">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopularPlansChart;
